
import { StreamingUtils } from '@app-libs/core';
import { Observable } from 'rxjs';
import { ApiClient } from './api-client';

/**
 * Base class for API services that need streaming capabilities.
 * Extends ApiClient with reusable streaming logic.
 */
export abstract class StreamingApiClient extends ApiClient {
  /**
   * Creates a streaming request with proper error handling and notifications.
   * @param url The URL to stream from
   * @param body The request body
   * @param options Additional options for error handling
   * @returns Observable stream of response chunks
   */
  protected createStreamingRequest(
    url: string,
    body: any,
    options: { showNotifications?: boolean } = { showNotifications: true },
  ): Observable<any> {
    return new Observable((observer) => {
      this.fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(async (response) => {
          if (!response.ok) {
            const error = await this.handleFetchError(response, options.showNotifications);
            throw error;
          }

          return this.handleStreamingResponse(response, observer);
        })
        .catch((error) => {
          // If it's already a FetchError, use it directly
          if (error.status !== undefined) {
            observer.error(error);
          } else {
            // For other errors (network, etc.), create a generic error
            const genericError = this.handleGenericError(error, options.showNotifications);
            observer.error(genericError);
          }
        });
    });
  }

  /**
   * Handles a streaming response from the server.
   * @param response The fetch response
   * @param observer The RxJS observer
   */
  private async handleStreamingResponse(response: Response, observer: any): Promise<void> {
    if (!response.body) {
      const error = new Error('ReadableStream is not supported in this environment.');
      this.notificationService.showError('Streaming is not supported in this browser');
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      await this.processStreamChunks(reader, decoder, observer);
    } catch (error) {
      console.error('Stream reading error:', error);
      this.notificationService.showError('Error reading response stream');
      observer.error(error);
    }
  }

  /**
   * Processes chunks from a ReadableStream.
   * @param reader The stream reader
   * @param decoder The text decoder
   * @param observer The RxJS observer
   */
  private async processStreamChunks(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    observer: any,
  ): Promise<void> {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        observer.complete();
        return;
      }

      // Decode the chunk
      const chunk = decoder.decode(value, { stream: true });
      // console.log('Received stream chunk:', chunk);

      // Extract individual chunks (handles buffered responses)
      const extractedChunks = StreamingUtils.extractChunks(chunk);
      // console.log('Extracted chunks:', extractedChunks);

      // Emit each chunk
      extractedChunks.forEach((extractedChunk) => {
        observer.next(extractedChunk);
      });
    }
  }

  /**
   * Helper method to create a simple POST request (non-streaming).
   * Uses fetchWithAuth for consistency with streaming requests.
   * @param url The URL to post to
   * @param body The request body
   * @returns Promise of the response
   */
  protected async fetchPost<T>(url: string, body: any): Promise<T> {
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await this.handleFetchError(response);
      throw error;
    }

    return response.json();
  }
}
